import type { EnvironmentType } from '@post-office/shared-contracts/execution-runtime';
import type { RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context/requestContext';
import { type BackendMessageBody } from '@post-office/placement-common-v2/types';
import { type SerializableRecord } from '@post-office/serializable-record';
import { getJiraOnboardingUrl } from '@post-office/message-templates-common--growth-starburst/utils/get-jira-onboarding-url';
import { type RedirectTo } from '@post-office/message-templates-common--growth-starburst/utils/resolve-redirect-to';

import { getConfluenceOnboardingUrl } from '../placements/in-app-v2/confluence-side-nav/get-confluence-onboarding-url';

export {
	resolveRedirectTo,
	type RedirectTo,
} from '@post-office/message-templates-common--growth-starburst/utils/resolve-redirect-to';

const LOOM_ONBOARDING_URL_PROD = 'https://www.loom.com/home';
const LOOM_ONBOARDING_URL_NON_PROD = 'https://stage.loom.com/home';

export const getLoomOnboardingUrl = (environmentType: EnvironmentType): string => {
	return environmentType === 'Production' ? LOOM_ONBOARDING_URL_PROD : LOOM_ONBOARDING_URL_NON_PROD;
};

type GetOnboardingUrlParams = {
	redirectTo: RedirectTo;
	requestContext: RequestContext;
	messageInstanceId: string;
	messageTemplateId: string;
	orgId?: string;
	twcEntitlementId?: string;
	triggerId?: string;
};

/**
 * Returns the onboarding URL based on `redirectTo`.
 *
 * For `jira`      → fetches the Jira onboarding URL via GUPS.
 * For `confluence` → fetches the Confluence onboarding URL via TCS + TAP.
 */
export const getOnboardingUrl = async ({
	redirectTo,
	requestContext,
	messageInstanceId,
	messageTemplateId,
	orgId,
	twcEntitlementId,
	triggerId,
}: GetOnboardingUrlParams): Promise<string | undefined> => {
	switch (redirectTo) {
		case 'jira':
			return getJiraOnboardingUrl(
				requestContext,
				orgId,
				twcEntitlementId,
				messageInstanceId,
				messageTemplateId,
			);

		case 'confluence':
			return getConfluenceOnboardingUrl(requestContext, {
				messageTemplateId,
				messageInstanceId,
				triggerId: triggerId ?? '',
			} as BackendMessageBody<SerializableRecord>);

		default: {
			const _exhaustive: never = redirectTo;
			return _exhaustive;
		}
	}
};
